import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Trash, Plus } from "react-bootstrap-icons";
import Loading from "../assetLibComponents/Loading";
import Toast from "../assetLibComponents/shared/Toast";
import {
  createRecommendedQuestion,
  createRecommendedQuestionSection,
  deleteRecommendedQuestion,
  deleteRecommendedQuestionSection,
  getRecommendedQuestions,
  updateRecommendedQuestion,
  updateRecommendedQuestionSection,
} from "../../api/customerSiaApi";

const makeTempId = () => `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const SettingRecommendedQuestions = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [rows, setRows] = useState([]);

  const sectionsData = useMemo(() => {
    const grouped = {};
    sections
      .filter((s) => !s.deleted)
      .forEach((s) => {
        grouped[s.id] = [];
      });
    rows.forEach((r) => {
      if (r.deleted) return;
      if (!grouped[r.section_id]) grouped[r.section_id] = [];
      grouped[r.section_id].push(r);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    });
    return grouped;
  }, [sections, rows]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRecommendedQuestions(authToken);
      if (res.data?.statusCode === "200") {
        const hydratedSections = (res.data.sections || []).map((s) => ({
          id: s.id,
          name: s.name || "",
          priority: s.priority || 0,
          is_active: s.is_active !== false,
          isNew: false,
          isDirty: false,
          isEditing: false,
          deleted: false,
        }));
        const hydratedRows = (res.data.details || []).map((item) => ({
          id: item.id,
          section_id: item.section_id,
          question: item.question || "",
          priority: item.priority || 0,
          is_active: item.is_active !== false,
          isNew: false,
          isDirty: false,
          isEditing: false,
          deleted: false,
        }));
        setSections(hydratedSections);
        setRows(hydratedRows);
      } else {
        Toast(res.data?.message || "Failed to load questions", "E");
      }
    } catch (e) {
      Toast(e?.response?.data?.message || "Failed to load questions", "E");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        id: makeTempId(),
        name: "",
        priority: prev.length,
        is_active: true,
        isNew: true,
        isDirty: true,
        isEditing: true,
        deleted: false,
      },
    ]);
  };

  const setSection = (id, updates) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates, isDirty: true } : s))
    );
  };

  const removeSection = (id) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, deleted: true, isDirty: true } : s))
    );
    setRows((prev) =>
      prev.map((r) => (r.section_id === id ? { ...r, deleted: true, isDirty: true } : r))
    );
  };

  const addQuestion = (sectionId) => {
    setRows((prev) => [
      ...prev,
      {
        id: makeTempId(),
        section_id: sectionId,
        question: "",
        priority: 0,
        is_active: true,
        isNew: true,
        isDirty: true,
        isEditing: true,
        deleted: false,
      },
    ]);
  };

  const setRow = (id, updates) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates, isDirty: true } : r))
    );
  };

  const removeRow = (id) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, deleted: true, isDirty: true } : r))
    );
  };

  const saveChanges = async () => {
    const activeSections = sections.filter((s) => !s.deleted);
    if (activeSections.length === 0) {
      Toast("At least one section is required.", "E");
      return;
    }
    for (const section of activeSections) {
      if (!section.name || !section.name.trim()) {
        Toast("Section name cannot be empty.", "E");
        return;
      }
    }
    const questionCandidates = rows.filter((r) => !r.deleted);
    for (const q of questionCandidates) {
      if (!q.question || !q.question.trim()) {
        Toast("Question text cannot be empty.", "E");
        return;
      }
    }

    setSaving(true);
    try {
      const deletedSections = sections.filter((s) => s.deleted && !s.isNew && typeof s.id === "number");
      for (const section of deletedSections) {
        await deleteRecommendedQuestionSection(authToken, section.id);
      }

      const sectionIdMap = {};
      const orderedSections = [...activeSections];
      for (let i = 0; i < orderedSections.length; i += 1) {
        const section = orderedSections[i];
        const payload = {
          name: section.name.trim(),
          priority: i,
          is_active: section.is_active !== false,
        };
        if (section.isNew) {
          const res = await createRecommendedQuestionSection(authToken, payload);
          sectionIdMap[section.id] = res.data?.data?.id;
        } else {
          sectionIdMap[section.id] = section.id;
          if (section.isDirty || section.priority !== i) {
            await updateRecommendedQuestionSection(authToken, { id: section.id, ...payload });
          }
        }
      }

      const toDeleteRows = rows.filter((r) => r.deleted && !r.isNew && typeof r.id === "number");
      for (const row of toDeleteRows) {
        await deleteRecommendedQuestion(authToken, row.id);
      }

      for (const section of orderedSections) {
        const resolvedSectionId = sectionIdMap[section.id];
        const sectionRows = (sectionsData[section.id] || []).filter((r) => !r.deleted);
        for (let i = 0; i < sectionRows.length; i += 1) {
          const row = sectionRows[i];
          const payload = {
            section_id: resolvedSectionId,
            question: row.question.trim(),
            priority: i,
            is_active: row.is_active !== false,
          };
          if (row.isNew) {
            await createRecommendedQuestion(authToken, payload);
          } else if (row.isDirty || row.priority !== i || row.section_id !== resolvedSectionId) {
            await updateRecommendedQuestion(authToken, { id: row.id, ...payload });
          }
        }
      }

      Toast("Recommended questions saved successfully.", "S");
      await loadData();
    } catch (e) {
      Toast(e?.response?.data?.message || "Failed to save changes", "E");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div className="px-0 max-w-5xl">
        <div className="flex justify-between mb-6 border-b border-gray-200 pb-4">
          <div className="text-lg leading-7 font-[500]">Recommended Questions</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={addSection}
              className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Plus size={14} /> Add Section
            </button>
            <button
              type="button"
              onClick={saveChanges}
              disabled={saving}
              className="rounded-md bg-[var(--brand-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[color-mix(in_srgb,var(--brand-primary)_82%,white)] disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="space-y-5">
          {sections
            .filter((section) => !section.deleted)
            .sort((a, b) => (a.priority || 0) - (b.priority || 0))
            .map((section) => {
              const list = sectionsData[section.id] || [];
              return (
                <div key={section.id} className="bg-white rounded-md border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3 gap-2">
                    {section.isEditing ? (
                      <input
                        value={section.name}
                        onChange={(e) => setSection(section.id, { name: e.target.value })}
                        className="text-[17px] font-[600] text-gray-800 border border-gray-300 rounded px-2 py-1"
                        placeholder="Section title"
                      />
                    ) : (
                      <h3 className="text-[17px] font-[600] text-gray-800">{section.name}</h3>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSection(section.id, { isEditing: !section.isEditing })}
                        className="text-gray-400 hover:text-gray-700"
                        title="Edit section"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSection(section.id)}
                        className="text-[#ef8b8b] hover:text-red-600"
                        title="Delete section"
                      >
                        <Trash size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => addQuestion(section.id)}
                        className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Plus size={14} /> Add Question
                      </button>
                    </div>
                  </div>

                  {list.length === 0 ? (
                    <div className="rounded border border-gray-200 bg-[#f7fbfc] text-center text-sm text-gray-500 py-8">
                      No questions added yet. Click "Add Question" to create one.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {list.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center gap-2 rounded border border-gray-200 bg-[#f7fbfc] px-3 py-2"
                        >
                          {row.isEditing ? (
                            <input
                              value={row.question}
                              onChange={(e) => setRow(row.id, { question: e.target.value })}
                              className="flex-1 bg-transparent border-0 text-sm text-gray-800 focus:ring-0"
                              placeholder="Enter question"
                            />
                          ) : (
                            <div className="flex-1 text-sm text-gray-800">{row.question}</div>
                          )}

                          <button
                            type="button"
                            onClick={() => setRow(row.id, { isEditing: !row.isEditing })}
                            className="text-gray-400 hover:text-gray-700"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeRow(row.id)}
                            className="text-[#ef8b8b] hover:text-red-600"
                            title="Delete"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};

export default SettingRecommendedQuestions;
